import { CompanyLogoCircle } from '@/components/company-logo-circle';
import { ApiError } from '@/lib/api';
import { blobToDataUrl } from '@/lib/data-url';
import {
  clampLogoFraming,
  fitLogoFile,
  formatFileSize,
  formatZoom,
  logoFileProblem,
  logoFileType,
  sameLogoFraming,
} from '@/lib/logo-framing';
import { useLogoDisplayUrl } from '@/lib/use-logo-display-url';
import { cn } from '@/lib/utils';
import {
  type CompanyLogo,
  LOGO_FIT_RATIO,
  LOGO_REJECTION_CODES,
  LOGO_ZOOM_MAX,
  LOGO_ZOOM_MIN,
  type LogoFraming,
  type LogoRejectionCode,
  NEUTRAL_LOGO_FRAMING,
} from '@mss_creator/shared';
import {
  ActionBar,
  Alert,
  Button,
  Dialog,
  DialogClose,
  Icon,
  Spinner,
  TextField,
} from '@pms-digital/design-system';
import {
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

export const LOGO_URL_ERROR_MESSAGE =
  'Impossible de récupérer cette image. Vérifiez le lien, ou importez le fichier.';
export const LOGO_FORMAT_ERROR_MESSAGE = 'Format non pris en charge. PNG, JPEG, WebP ou SVG.';
export const LOGO_TOO_LARGE_ERROR_MESSAGE = 'Fichier trop volumineux - 2 Mo maximum.';
export const LOGO_SVG_UNSAFE_ERROR_MESSAGE =
  'Ce SVG contient du code actif (script, lien externe…) et ne peut pas être utilisé. Réexportez-le en SVG simple, ou importez un PNG.';
export const LOGO_SVG_NO_SIZE_ERROR_MESSAGE =
  "Ce SVG n'indique pas ses dimensions : réexportez-le avec une viewBox, ou importez un PNG.";
export const LOGO_SAVE_ERROR_MESSAGE = "Le logo n'a pas pu être enregistré. Réessayez.";

const FILE_ERROR_MESSAGES: Readonly<Record<LogoRejectionCode, string>> = {
  LOGO_FORMAT_UNSUPPORTED: LOGO_FORMAT_ERROR_MESSAGE,
  LOGO_TOO_LARGE: LOGO_TOO_LARGE_ERROR_MESSAGE,
  LOGO_URL_UNREACHABLE: LOGO_URL_ERROR_MESSAGE,
  LOGO_SVG_UNSAFE: LOGO_SVG_UNSAFE_ERROR_MESSAGE,
  LOGO_SVG_NO_SIZE: LOGO_SVG_NO_SIZE_ERROR_MESSAGE,
};

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg';

/** The editor's circle: 240 px inside a 300 px stage, as drawn on planche 06. */
const STAGE_PX = 300;
const CIRCLE_PX = 240;
/** A keyboard arrow moves the image by this many pixels of the editor, 5 times that with Shift. */
const ARROW_STEP_PX = 4;
const ARROW_SHIFT_FACTOR = 5;
const ZOOM_BUTTON_STEP = 0.25;

/** An image the dialog can frame, and where to find it. */
export interface LogoDialogImage {
  /** Something an `<img>` can show: a stored logo's path, a URL, or a `data:` URL of the chosen file. */
  src: string;
  /** The file to send when saving, or null when the image is already stored (the current logo, or one just downloaded). */
  file: File | null;
  /** Shown under the previews. */
  name: string;
  /** « PNG, 14 Ko », « récupéré depuis exemple.fr ». */
  detail: string;
}

export interface CompanyLogoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The logo the company has now: the dialog then opens on the framing step. */
  current: CompanyLogo | null;
  /** « Récupérer »: fetch the image behind a URL. Rejects when it cannot be fetched. */
  onImportUrl: (url: string) => Promise<LogoDialogImage>;
  /** « Enregistrer »: persist (or, on the creation form, keep) the image and its framing. */
  onSave: (result: { file: File | null; framing: LogoFraming }) => Promise<void>;
  /** « Retirer le logo »; the button is only offered when the company has a logo and this is given. */
  onRemove?: () => Promise<void>;
}

/** The `LOGO_*` refusal an API call came back with, if that is what it was. */
function rejectionOf(error: unknown): LogoRejectionCode | null {
  if (!(error instanceof ApiError)) return null;
  return LOGO_REJECTION_CODES.find((code) => code === error.code) ?? null;
}

function typeLabel(file: File): string {
  const subtype = file.type.split('/')[1]?.split('+')[0]?.toUpperCase();
  return subtype && subtype.length > 0 ? subtype : 'Image';
}

/** What the file refused in the sources step was, so the zone can name it. */
interface RefusedFile {
  code: LogoRejectionCode;
  name: string;
}

/**
 * The company logo editor (issue #20, planche 06): two sources shown together
 * (a file, or the address of an image the server downloads once), then the
 * framing — the whole logo in the circle to begin with, then zoom 1x to 4x and
 * drag (or the arrow keys) to choose what stays visible, with the previews at
 * the sizes it will be drawn at. Only the framing is saved, never a cropped
 * image. Mounted anew at each opening, so each starts from the current logo.
 */
export function CompanyLogoDialog(props: CompanyLogoDialogProps) {
  const { open } = props;
  if (!open) return null;
  return <LogoDialogBody {...props} />;
}

function LogoDialogBody({
  onOpenChange,
  current,
  onImportUrl,
  onSave,
  onRemove,
}: CompanyLogoDialogProps) {
  const [image, setImage] = useState<LogoDialogImage | null>(() =>
    current ? { src: current.src, file: null, name: 'Logo actuel', detail: '' } : null,
  );
  const [framing, setFraming] = useState<LogoFraming>(() =>
    current
      ? { zoom: current.zoom, offsetX: current.offsetX, offsetY: current.offsetY }
      : NEUTRAL_LOGO_FRAMING,
  );
  // The sources step is shown until an image is chosen; « Changer d'image » returns to it.
  const [picking, setPicking] = useState(current === null);
  const [refusedFile, setRefusedFile] = useState<RefusedFile | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function chooseImage(next: LogoDialogImage) {
    setImage(next);
    setFraming(NEUTRAL_LOGO_FRAMING);
    setRefusedFile(null);
    setSaveError(null);
    setPicking(false);
  }

  async function chooseFile(file: File) {
    const problem = logoFileProblem(file);
    if (problem) {
      setRefusedFile({ code: problem, name: file.name });
      return;
    }
    // An SVG without a viewBox is given the one its size calls for, or refused.
    const fitted = await fitLogoFile(file);
    if ('problem' in fitted) {
      setRefusedFile({ code: fitted.problem, name: file.name });
      return;
    }
    // A data URL, not an object URL: see `lib/data-url.ts`.
    const src = await blobToDataUrl(new Blob([fitted.file], { type: logoFileType(fitted.file) }));
    chooseImage({
      src,
      file: fitted.file,
      name: file.name,
      detail: `${typeLabel(file)}, ${formatFileSize(file.size)}`,
    });
  }

  async function handleImportUrl(url: string) {
    chooseImage(await onImportUrl(url));
  }

  const unchanged =
    image !== null &&
    image.file === null &&
    current !== null &&
    image.src === current.src &&
    sameLogoFraming(framing, current);

  async function handleSave() {
    if (!image || saving) return;
    if (unchanged) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ file: image.file, framing });
      onOpenChange(false);
    } catch (error) {
      const code = rejectionOf(error);
      if (
        code === 'LOGO_FORMAT_UNSUPPORTED' ||
        code === 'LOGO_TOO_LARGE' ||
        code === 'LOGO_SVG_UNSAFE' ||
        code === 'LOGO_SVG_NO_SIZE'
      ) {
        // The browser let the file through, the server read its content and
        // refused it: back to the sources, naming the file.
        setRefusedFile({ code, name: image.file?.name ?? image.name });
        setImage(null);
        setPicking(true);
      } else {
        setSaveError(LOGO_SAVE_ERROR_MESSAGE);
      }
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!onRemove || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onRemove();
      onOpenChange(false);
    } catch {
      setSaveError(LOGO_SAVE_ERROR_MESSAGE);
      setSaving(false);
    }
  }

  const framingStep = !picking && image !== null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && saving) return;
        onOpenChange(next);
      }}
      title="Logo de l'entreprise"
      description={
        framingStep
          ? 'Choisissez la partie visible dans le cercle.'
          : 'Importez une image ou indiquez son adresse.'
      }
      size="form"
      actions={
        <ActionBar
          secondary={
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={saving}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              variant="secondary"
              type="button"
              disabled={!framingStep}
              loading={saving}
              loadingLabel="Enregistrement…"
              onClick={() => void handleSave()}
            >
              Enregistrer
            </Button>
          }
        />
      }
    >
      {framingStep ? (
        <FramingStep
          image={image}
          framing={framing}
          onFramingChange={setFraming}
          onChangeImage={() => setPicking(true)}
        />
      ) : (
        <SourcesStep
          refusedFile={refusedFile}
          onFile={chooseFile}
          onDismissRefusal={() => setRefusedFile(null)}
          onImportUrl={handleImportUrl}
          onBack={current !== null && image !== null ? () => setPicking(false) : null}
        />
      )}
      {saveError && <Alert tone="danger">{saveError}</Alert>}

      {current !== null && onRemove && (
        <div className="mt-4">
          <Button
            type="button"
            variant="ghost"
            icon="delete"
            disabled={saving}
            onClick={() => void handleRemove()}
          >
            Retirer le logo
          </Button>
        </div>
      )}
    </Dialog>
  );
}

// ── Step 1: the two sources ──────────────────────────────────────────────────

interface SourcesStepProps {
  refusedFile: RefusedFile | null;
  onFile: (file: File) => Promise<void>;
  /** An address is being tried: the refusal of a file no longer applies. */
  onDismissRefusal: () => void;
  onImportUrl: (url: string) => Promise<void>;
  /** Back to the framing of the image already chosen; null when there is none. */
  onBack: (() => void) | null;
}

function SourcesStep({
  refusedFile,
  onFile,
  onDismissRefusal,
  onImportUrl,
  onBack,
}: SourcesStepProps) {
  const urlId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [url, setUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // One error at a time: a file that was just refused replaces the address's.
  useEffect(() => {
    if (refusedFile) setUrlError(null);
  }, [refusedFile]);

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) void onFile(file);
  }

  async function fetchUrl() {
    const trimmed = url.trim();
    if (trimmed.length === 0 || fetching) return;
    setFetching(true);
    setUrlError(null);
    onDismissRefusal();
    try {
      await onImportUrl(trimmed);
    } catch (error) {
      // 404, redirect, not an image, too heavy, too slow: one message for all;
      // an SVG with active content, or without a size, is told apart, since
      // another address will not help.
      const code = rejectionOf(error);
      setUrlError(
        code === 'LOGO_SVG_UNSAFE'
          ? LOGO_SVG_UNSAFE_ERROR_MESSAGE
          : code === 'LOGO_SVG_NO_SIZE'
            ? LOGO_SVG_NO_SIZE_ERROR_MESSAGE
            : LOGO_URL_ERROR_MESSAGE,
      );
      setFetching(false);
    }
  }

  return (
    <div>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-4 text-center',
          refusedFile
            ? 'border-danger bg-danger-tint'
            : dragOver
              ? 'border-bleu-nuit bg-bleu-nuit-tint'
              : 'border-gray-300 bg-white',
        )}
      >
        <span
          className={cn(
            'flex size-12 items-center justify-center rounded-full',
            refusedFile ? 'bg-white text-rouge-deep' : 'bg-bleu-nuit-tint text-bleu-nuit',
          )}
        >
          {refusedFile ? <Icon name="error" size={16} /> : <Icon name="upload" size={16} />}
        </span>
        {refusedFile ? (
          <>
            <p role="alert" className="font-semibold text-body text-noir">
              {FILE_ERROR_MESSAGES[refusedFile.code]}
            </p>
            <p className="text-gray-600 text-small">
              Glissez une autre image ou{' '}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-bleu-nuit underline underline-offset-2"
              >
                parcourez
              </button>
            </p>
            <p className="inline-flex items-center gap-1 text-gray-700 text-small">
              <Icon name="image" size={14} />
              {refusedFile.name}
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold text-body text-noir">
              Glissez une image ou{' '}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-bleu-nuit underline underline-offset-2"
              >
                parcourez
              </button>
            </p>
            <p className="text-gray-600 text-small">PNG, JPEG, WebP ou SVG, 2 Mo maximum</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          aria-label="Choisir une image"
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset so that choosing the same file again still fires.
            event.target.value = '';
            if (file) void onFile(file);
          }}
        />
      </div>

      <div className="my-4 flex items-center gap-3 font-semibold text-gray-500 text-small">
        <span className="h-px flex-1 bg-gray-100" aria-hidden="true" />
        ou
        <span className="h-px flex-1 bg-gray-100" aria-hidden="true" />
      </div>

      <div className="flex items-end gap-2">
        <TextField
          label="Adresse de l'image"
          id={urlId}
          type="url"
          placeholder="https://…"
          status={urlError ? 'error' : 'default'}
          value={url}
          disabled={fetching}
          onChange={(event) => {
            setUrl(event.target.value);
            setUrlError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void fetchUrl();
            }
          }}
        />
        <Button
          type="button"
          variant="primary"
          disabled={fetching || url.trim().length === 0}
          onClick={() => void fetchUrl()}
        >
          {fetching ? (
            <>
              <Spinner size="xs" label="" />
              Récupération…
            </>
          ) : (
            'Récupérer'
          )}
        </Button>
      </div>
      {urlError ? (
        <p role="alert" className="mt-1 flex items-start gap-1 text-danger text-small">
          <Icon name="error" size={14} />
          {urlError}
        </p>
      ) : (
        <p className="mt-1 text-gray-600 text-small">
          Une copie de l'image est enregistrée : le logo ne dépend plus du site d'origine.
        </p>
      )}
      {onBack && (
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          Revenir au cadrage
        </Button>
      )}
    </div>
  );
}

// ── Step 2: the framing ──────────────────────────────────────────────────────

interface FramingStepProps {
  image: LogoDialogImage;
  framing: LogoFraming;
  onFramingChange: (framing: LogoFraming) => void;
  onChangeImage: () => void;
}

function FramingStep({ image, framing, onFramingChange, onChangeImage }: FramingStepProps) {
  // The image's own proportions decide how far it may be dragged. Unknown until
  // it has loaded (and for an SVG that states none): then it is taken as square.
  const [aspect, setAspect] = useState(1);
  const displayUrl = useLogoDisplayUrl(image.src);
  const dragStart = useRef<{ x: number; y: number; framing: LogoFraming } | null>(null);

  const boxPx = framing.zoom * LOGO_FIT_RATIO * CIRCLE_PX;

  function update(next: LogoFraming, nextAspect = aspect) {
    onFramingChange(clampLogoFraming(next, nextAspect));
  }

  function handleImageLoad(element: HTMLImageElement) {
    const { naturalWidth, naturalHeight } = element;
    if (naturalWidth > 0 && naturalHeight > 0) {
      const next = naturalWidth / naturalHeight;
      setAspect(next);
      update(framing, next);
    }
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    dragStart.current = { x: event.clientX, y: event.clientY, framing };
    const move = (moveEvent: PointerEvent) => {
      const start = dragStart.current;
      if (!start) return;
      const startBox = start.framing.zoom * LOGO_FIT_RATIO * CIRCLE_PX;
      update({
        zoom: start.framing.zoom,
        offsetX: start.framing.offsetX + (moveEvent.clientX - start.x) / startBox,
        offsetY: start.framing.offsetY + (moveEvent.clientY - start.y) / startBox,
      });
    };
    const end = () => {
      dragStart.current = null;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = ARROW_STEP_PX * (event.shiftKey ? ARROW_SHIFT_FACTOR : 1);
    const delta = step / boxPx;
    // The arrow moves the image the way the finger would: left takes it left.
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-delta, 0],
      ArrowRight: [delta, 0],
      ArrowUp: [0, -delta],
      ArrowDown: [0, delta],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    update({ ...framing, offsetX: framing.offsetX + move[0], offsetY: framing.offsetY + move[1] });
  }

  function setZoom(zoom: number) {
    update({ ...framing, zoom });
  }

  const preview = { src: image.src, ...framing };
  const ghostLeft = STAGE_PX / 2 + framing.offsetX * boxPx - boxPx / 2;
  const ghostTop = STAGE_PX / 2 + framing.offsetY * boxPx - boxPx / 2;

  return (
    <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
      <div>
        {/* biome-ignore lint/a11y/useSemanticElements: a drag surface with arrow-key support, no native element fits */}
        <div
          role="group"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the arrow keys move the image, so the surface has to take the focus
          tabIndex={0}
          aria-label="Cadrage : glisser ou flèches du clavier pour déplacer"
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          className="relative cursor-grab touch-none select-none overflow-hidden rounded-md bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit active:cursor-grabbing"
          style={{ width: STAGE_PX, height: STAGE_PX }}
        >
          {displayUrl && (
            <img
              src={displayUrl}
              alt=""
              draggable={false}
              onLoad={(event) => handleImageLoad(event.currentTarget)}
              className="pointer-events-none absolute max-w-none object-contain opacity-50"
              style={{ left: ghostLeft, top: ghostTop, width: boxPx, height: boxPx }}
            />
          )}
          <CompanyLogoCircle
            logo={preview}
            alt="Aperçu du logo dans le cercle"
            className="pointer-events-none absolute top-[30px] left-[30px] size-60 shadow-[0_0_0_2px_var(--color-blanc),0_0_0_9999px_color-mix(in_oklab,var(--color-gray-100)_55%,transparent)]" // pms-design-exception: cadre de recadrage du logo : voile et position en pixels du cercle de 240 px
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-gray-600" style={{ width: STAGE_PX }}>
          <button
            type="button"
            aria-label="Dézoomer"
            onClick={() => setZoom(framing.zoom - ZOOM_BUTTON_STEP)}
            className="rounded p-0 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            <Icon name="zoom_out" size={16} />
          </button>
          <input
            type="range"
            aria-label="Zoom"
            min={LOGO_ZOOM_MIN}
            max={LOGO_ZOOM_MAX}
            step={0.05}
            value={framing.zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className="h-1 flex-1 cursor-pointer accent-bleu-nuit"
          />
          <button
            type="button"
            aria-label="Zoomer"
            onClick={() => setZoom(framing.zoom + ZOOM_BUTTON_STEP)}
            className="rounded p-0 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            <Icon name="zoom_in" size={16} />
          </button>
          <output
            aria-label="Niveau de zoom"
            className="w-9 text-right font-semibold text-bleu-nuit text-small"
          >
            {formatZoom(framing.zoom)}
          </output>
        </div>
        <p
          className="mt-2 flex items-center gap-1 text-gray-600 text-small"
          style={{ width: STAGE_PX }}
        >
          <Icon name="open_with" size={14} />
          Glissez l'image pour la déplacer
        </p>
      </div>

      <div className="min-w-0 flex-1">
        <h4 className="mb-2 font-semibold text-small">Aperçu</h4>
        <div className="flex items-end gap-4 rounded-md border border-gray-100 bg-gray-50 p-4">
          <figure className="m-0 flex flex-col items-center gap-1 text-caption text-gray-600">
            <CompanyLogoCircle logo={preview} alt="Aperçu 64 px" />
            <figcaption>Carte - 64 px</figcaption>
          </figure>
          <figure className="m-0 flex flex-col items-center gap-1 text-caption text-gray-600">
            <CompanyLogoCircle logo={preview} alt="Aperçu 80 px" className="size-20" />
            <figcaption>Paramètres - 80 px</figcaption>
          </figure>
        </div>
        <div className="mt-4 border-gray-100 border-t pt-4 text-gray-700 text-small">
          <p className="mb-2 flex items-start gap-1">
            <Icon name="image" size={14} />
            <span>
              <b>{image.name}</b>
              {image.detail ? ` - ${image.detail}` : ''}
            </span>
          </p>
          <Button type="button" variant="primary" size="sm" onClick={onChangeImage}>
            <Icon name="image" size={16} />
            Changer d'image
          </Button>
        </div>
      </div>
    </div>
  );
}
